/**
 * Wizard state → exact Apple declarative config objects.
 *
 * Critical contract details (from §5b of the spec):
 *   - `TargetLocalDateTime` MUST be `YYYY-MM-DDTHH:MM:SS` (no Z/offset/ms).
 *   - `TargetOSVersion` MUST match `^\d+(\.\d+){0,2}$`.
 *   - Omit empty optional keys entirely. Don't send `""` or empty objects.
 *   - `InstallSecurityUpdate` is SINGULAR.
 *   - `RecommendedCadence` is TOP-LEVEL, not nested under Deferrals.
 *   - Beta: never send both `OfferPrograms` and `RequireProgram`.
 */

import type {
  PinnedConfigState,
  RollingConfigState,
  SoftwareUpdateWizardState,
  SuPlatform,
  UpdateBehaviorState,
} from "../types";

export const TARGET_VERSION_RE = /^\d+(\.\d+){0,2}$/;
export const TARGET_LOCAL_DT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

/**
 * datetime-local inputs emit `YYYY-MM-DDTHH:MM`. Some pickers emit fractional
 * seconds or a trailing `Z`. Coerce to the strict Apple shape.
 */
export function normalizeTargetLocalDateTime(raw: string): string {
  if (!raw) return "";
  // strip Z, timezone offset (±HH:MM), fractional ms
  let s = raw.trim();
  s = s.replace(/\.\d+/, "");
  s = s.replace(/(Z|[+-]\d{2}:?\d{2})$/, "");
  // pad seconds if missing: YYYY-MM-DDTHH:MM → YYYY-MM-DDTHH:MM:00
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) s += ":00";
  return s;
}

export function isValidTargetVersion(v: string): boolean {
  return TARGET_VERSION_RE.test(v.trim());
}

export function isValidDeadline(v: string): boolean {
  return TARGET_LOCAL_DT_RE.test(normalizeTargetLocalDateTime(v));
}

// ───── Mode A: Pinned ─────────────────────────────────────────────────────

export function buildPinnedConfig(p: PinnedConfigState): Record<string, unknown> {
  const cfg: Record<string, unknown> = {
    TargetOSVersion: p.targetVersion.trim(),
    TargetLocalDateTime: normalizeTargetLocalDateTime(p.deadline),
  };
  if (p.targetBuild.trim()) cfg.TargetBuildVersion = p.targetBuild.trim();
  if (p.detailsUrl.trim()) cfg.DetailsURL = p.detailsUrl.trim();
  return cfg;
}

// ───── Mode B: Rolling (interim — Pinned snapshot) ───────────────────────

export function buildRollingSnapshotConfig(
  r: RollingConfigState,
  deadline: string,
): Record<string, unknown> {
  // Pragmatic interim: write the typed snapshot version as Pinned.
  return buildPinnedConfig({
    targetVersion: r.snapshotVersion,
    targetBuild: "",
    deadline,
    detailsUrl: "",
  });
}

// ───── Mode C: Update Behavior ───────────────────────────────────────────

function clampDeferral(n: number): number | null {
  const v = Math.floor(Number(n) || 0);
  if (v < 1 || v > 90) return null;
  return v;
}

export function buildUpdateBehaviorConfig(
  b: UpdateBehaviorState,
  platform: SuPlatform,
): Record<string, unknown> {
  const cfg: Record<string, unknown> = {};

  // Deferrals — platform-scoped
  const deferrals: Record<string, number> = {};
  if (platform === "macos") {
    const major = clampDeferral(b.deferMajorDays);
    const minor = clampDeferral(b.deferMinorDays);
    const system = clampDeferral(b.deferSystemDays);
    if (major) deferrals.MajorPeriodInDays = major;
    if (minor) deferrals.MinorPeriodInDays = minor;
    if (system) deferrals.SystemPeriodInDays = system;
  } else {
    const combined = clampDeferral(b.deferCombinedDays);
    if (combined) deferrals.CombinedPeriodInDays = combined;
  }
  if (Object.keys(deferrals).length) cfg.Deferrals = deferrals;

  // RecommendedCadence — top-level, iOS family only
  if (platform !== "macos" && b.recommendedCadence) {
    cfg.RecommendedCadence = b.recommendedCadence;
  }

  // AutomaticActions
  const auto: Record<string, string> = {};
  if (b.autoDownload !== "Allowed") auto.Download = b.autoDownload;
  if (b.autoInstallOs !== "Allowed") auto.InstallOSUpdates = b.autoInstallOs;
  if (platform === "macos" && b.autoInstallSecurity !== "Allowed") {
    auto.InstallSecurityUpdate = b.autoInstallSecurity; // SINGULAR
  }
  if (Object.keys(auto).length) cfg.AutomaticActions = auto;

  // RapidSecurityResponse (iOS / macOS) — only emit when user diverged from default true
  if (platform === "ios" || platform === "macos") {
    const rsr: Record<string, boolean> = {};
    if (!b.rsrEnable) rsr.Enable = false;
    if (!b.rsrEnableRollback) rsr.EnableRollback = false;
    if (Object.keys(rsr).length) cfg.RapidSecurityResponse = rsr;
  }

  // Standard users — macOS only, default true
  if (platform === "macos" && !b.allowStandardUserOsUpdates) {
    cfg.AllowStandardUserOSUpdates = false;
  }

  // Notifications — default true
  if (!b.notifications) cfg.Notifications = false;

  // Beta — enforce mutual exclusivity
  const beta: Record<string, unknown> = {};
  if (b.betaEnrollment !== "Allowed") beta.ProgramEnrollment = b.betaEnrollment;
  const hasRequire =
    b.betaEnrollment === "AlwaysOn" &&
    b.betaRequireProgram &&
    b.betaRequireProgram.token.trim().length > 0;
  const hasOffer =
    b.betaEnrollment !== "AlwaysOff" &&
    !hasRequire &&
    b.betaOfferPrograms.some((p) => p.token.trim().length > 0);
  if (hasRequire) {
    beta.RequireProgram = {
      Description: b.betaRequireProgram!.description.trim(),
      Token: b.betaRequireProgram!.token.trim(),
    };
  } else if (hasOffer) {
    beta.OfferPrograms = b.betaOfferPrograms
      .filter((p) => p.token.trim().length > 0)
      .map((p) => ({ Description: p.description.trim(), Token: p.token.trim() }));
  }
  if (Object.keys(beta).length) cfg.Beta = beta;

  return cfg;
}

// ───── Top-level builder ──────────────────────────────────────────────────

export interface BuiltPayload {
  kind: "declaration";
  identifier: string;
  config: Record<string, unknown>;
}

export function buildPayload(state: SoftwareUpdateWizardState): BuiltPayload {
  if (state.mode === "pinned") {
    return {
      kind: "declaration",
      identifier: "com.apple.configuration.softwareupdate.enforcement.specific",
      config: buildPinnedConfig(state.pinned),
    };
  }
  if (state.mode === "rolling") {
    // Interim: snapshot deadline = today + delayDays at applyTime, device-local
    const dt = new Date();
    dt.setDate(dt.getDate() + (state.rolling.delayDays || 0));
    const [hh, mm] = (state.rolling.applyTime || "20:00").split(":");
    dt.setHours(parseInt(hh || "20", 10), parseInt(mm || "0", 10), 0, 0);
    const deadline =
      `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}` +
      `T${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}:00`;
    return {
      kind: "declaration",
      identifier: "com.apple.configuration.softwareupdate.enforcement.specific",
      config: buildRollingSnapshotConfig(state.rolling, deadline),
    };
  }
  return {
    kind: "declaration",
    identifier: "com.apple.configuration.softwareupdate.settings",
    config: buildUpdateBehaviorConfig(state.behavior, state.platform),
  };
}

/** Map platform → backend platform string used on CreatePolicyRequest.platforms. */
export function platformToPolicyPlatform(p: SuPlatform): string {
  return p === "macos" ? "MACOS" : p === "ios" ? "IOS" : p === "ipados" ? "IPADOS" : p === "tvos" ? "TVOS" : "VISIONOS";
}
